import { useState, type FormEvent } from 'react';
import { searchPaymentMembers, type PaymentMemberSearchItem } from '@urbanik/api-client';
import { Search, UserRoundCheck } from 'lucide-react';
import { apiBase } from './api-base';
import { MemberPaymentDraftForm } from './MemberPaymentDraftForm';
import './member-payment.css';
import './admin-payments.css';
import { ManualPaymentAllocation } from './ManualPaymentAllocation';
import { PaymentHistory } from './PaymentHistory';

export function AdminPaymentsView({ canApprove }: { canApprove: boolean }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly PaymentMemberSearchItem[]>([]);
  const [selected, setSelected] = useState<PaymentMemberSearchItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const search = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSelected(null);
    try {
      const response = await searchPaymentMembers(apiBase, query.trim());
      setResults(response.items);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const selectMember = (member: PaymentMemberSearchItem) => {
    setSelected(member);
    setResults([]);
    setQuery('');
  };
  return (
    <div className="admin-payments">
      <header className="page-heading">
        <div>
          <h1>Registrar comprobante</h1>
          <p>
            Registra un comprobante en favor de un socio. El saldo no cambia hasta su aprobación.
          </p>
        </div>
      </header>
      <section className="member-payment admin-payments__search">
        <header>
          <div>
            <span>SOCIO BENEFICIARIO</span>
            <h2>Buscar socio</h2>
          </div>
        </header>
        <form onSubmit={search}>
          <label>
            <span>Cédula, nombre o alícuota</span>
            <div>
              <Search />
              <input
                required
                minLength={2}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ej.: nombre, cédula o alícuota"
              />
              <button disabled={busy}>{busy ? 'Buscando…' : 'Buscar'}</button>
            </div>
          </label>
        </form>
        {error ? (
          <p className="member-payment__message member-payment__message--error">{error}</p>
        ) : null}
        {results.length ? (
          <ul>
            {results.map((member) => (
              <li key={member.id}>
                <button
                  type="button"
                  onClick={() => selectMember(member)}
                  aria-pressed={selected?.id === member.id}
                >
                  <UserRoundCheck />
                  <span>
                    <strong>{member.displayName}</strong>
                    <small>
                      {member.nationalId} · Alícuota {member.slotNumber ?? 'sin número'}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {selected ? (
        <>
          <div className="admin-payments__selected" role="status" aria-live="polite">
            <UserRoundCheck />
            <span>
              Registrar para <strong>{selected.displayName}</strong> · {selected.nationalId}
            </span>
          </div>
          <MemberPaymentDraftForm
            memberId={selected.id}
            memberNationalId={selected.nationalId}
            memberName={selected.displayName}
            showHistory={false}
            canApprove={canApprove}
          />
        </>
      ) : null}
      <ManualPaymentAllocation />
      <PaymentHistory />
    </div>
  );
}
